import React, { useState, useEffect } from 'react';
import { Button, DataTable, Toast, ConfigFilters } from '../../../shared/components';
import { Edit, Eye, Settings } from 'lucide-react';
import { useConfigManagement } from '../../../shared/hooks';
import { RoleProtectedRoute } from '../../../shared/components';
import ConfigFormModal from './ConfigFormModal';

/**
 * Lista de configuraciones del sistema con filtros avanzados
 * Permite filtrar por clave, estado y búsqueda
 */
const ConfigList = () => {
  const {
    configs,
    loading,
    error,
    filters,
    fetchConfigs,
    createConfig,
    updateConfig,
    applyConfigFilters,
    applyFiltersNow,
    clearFilters,
  } = useConfigManagement();

  // Estados de modales
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState(null);

  // Estados de notificaciones
  const [toast, setToast] = useState({
    isVisible: false,
    message: '',
    type: 'success'
  });

  // Cargar configuraciones al montar el componente
  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  // Mostrar notificación
  const showToast = (message, type = 'success') => {
    setToast({
      isVisible: true,
      message,
      type
    });
  };

  // Ocultar notificación
  const hideToast = () => {
    setToast(prev => ({ ...prev, isVisible: false }));
  };

  // Manejar búsqueda y filtros
  const handleSearch = async () => {
    applyFiltersNow();
  };

  // Manejar cambio de filtros (solo actualizar estado, no aplicar)
  const handleFiltersChange = (newFilters) => {
    applyConfigFilters(newFilters);
  };

  // Manejar limpieza de filtros
  const handleClearFilters = () => {
    clearFilters();
    fetchConfigs();
  };

  // Manejar apertura de modal de edición
  const handleEditConfig = (config) => {
    setSelectedConfig(config);
    setIsEditModalOpen(true);
  };

  // Manejar apertura de modal de visualización
  const handleViewConfig = (config) => {
    setSelectedConfig(config);
    setIsViewModalOpen(true);
  };

  // Manejar cierre de modales
  const handleCloseModals = () => {
    setIsEditModalOpen(false);
    setIsViewModalOpen(false);
    setSelectedConfig(null);
  };

  // Manejar actualización exitosa
  const handleUpdateSuccess = () => {
    handleCloseModals();
    showToast('Configuración actualizada exitosamente');
  };

  // Configurar columnas de la tabla
  const columns = [
    {
      key: 'description',
      label: 'Descripción',
      render: (config) => (
        <div className="text-gray-600 max-w-xs truncate" title={config.description}>
          {config.description || 'Sin descripción'}
        </div>
      )
    },
    {
      key: 'value',
      label: 'Valor',
      render: (config) => (
        <div className="text-gray-600 max-w-xs truncate" title={config.value}>
          {config.value}
        </div>
      )
    },
    {
      key: 'isActive',
      label: 'Estado',
      render: (config) => {
        const isActive = config.isActive;
        return (
          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
            isActive 
              ? 'bg-green-100 text-green-800' 
              : 'bg-red-100 text-red-800'
          }`}>
            {isActive ? 'Activo' : 'Inactivo'}
          </span>
        );
      }
    },
    {
      key: 'createdAt',
      label: 'Creado',
      render: (config) => (
        <div className="text-gray-600 text-sm">
          {new Date(config.createdAt).toLocaleDateString('es-ES')}
        </div>
      )
    },
    {
      key: 'actions',
      label: 'Acciones',
      render: (config) => (
        <div className="flex items-center space-x-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleViewConfig(config)}
            className="text-blue-600 hover:text-blue-800"
            title="Ver detalles"
          >
            <Eye className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleEditConfig(config)}
            className="text-green-600 hover:text-green-800"
            title="Editar"
          >
            <Edit className="w-4 h-4" />
          </Button>
        </div>
      )
    }
  ];

  return (
    <RoleProtectedRoute requiredRole="admin">
      <div className="space-y-6">
        {/* Header de la sección */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Settings className="w-6 h-6" />
              Configuración del Sistema
            </h1>
            <p className="text-gray-600 mt-1">
              Administra las configuraciones del sistema
            </p>
          </div>
        </div>

        {/* Filtros */}
        <ConfigFilters
          filters={filters}
          onFiltersChange={handleFiltersChange}
          onClearFilters={handleClearFilters}
          onSearch={handleSearch}
          searchPlaceholder="Buscar por clave..."
          loading={loading}
        />

        {/* Tabla de configuraciones */}
        <div className="bg-white rounded-lg shadow">
          <DataTable
            section="Configuraciones"
            data={configs}
            columns={columns}
            loading={loading}
            error={error}
            onRetry={fetchConfigs}
            emptyMessage="No se encontraron configuraciones"
          />
        </div>

        {/* Modals */}

        <ConfigFormModal
          isOpen={isEditModalOpen}
          onClose={handleCloseModals}
          onSuccess={handleUpdateSuccess}
          mode="edit"
          config={selectedConfig}
          createConfig={createConfig}
          updateConfig={updateConfig}
        />

        <ConfigFormModal
          isOpen={isViewModalOpen}
          onClose={handleCloseModals}
          mode="view"
          config={selectedConfig}
          createConfig={createConfig}
          updateConfig={updateConfig}
        />

        {/* Notificaciones */}
        <Toast
          isVisible={toast.isVisible}
          message={toast.message}
          type={toast.type}
          onClose={hideToast}
        />
      </div>
    </RoleProtectedRoute>
  );
};

export default ConfigList; 